/**
 * A team's repository, stored on the TEAM server-side (board card 84628c27,
 * 04.10.2026): `{ url, provider }` — a reference only, never a token or a
 * Vault item (the same rule as project code repos, graph/repoRefs.js). Every
 * member's device reads it and offers "Join"; each member signs in with their
 * OWN GitHub / GitLab account or a Vault item.
 *
 *   join     — connect this device to the team's stored repository: any member
 *   connect  — the first repository of a team that has none: owners/admins
 *   move     — a DIFFERENT repository than the stored one: owners/admins,
 *              after a warning ("Move the team to another repository")
 *
 * Pure — the git work stays in scopeGitSync.js, the server calls in
 * teamsService.js.
 */
import { hostOf, withoutUser } from '../../graph/repoRefs';
import { ownerRepoOf } from './providers/remote';

const TEAM = /^team:(.+)$/;

/** The team id of a scope key ("team:12" → "12"); null for any other scope. */
export const teamIdOf = (scopeKey) => TEAM.exec(String(scopeKey || ''))?.[1] || null;

export const isTeamAdminRole = (role) => role === 'owner' || role === 'admin';

/** "host/owner/repo" as written (".git" dropped) — what the team row shows. */
export function teamRepoLabel(teamRepo) {
  const url = teamRepo?.url || '';
  const host = hostOf(url);
  if (!host) return url;
  const { owner, repo } = ownerRepoOf(url);
  return [host, owner, repo].filter(Boolean).join('/');
}

/** The same repository whether it is reached over https or ssh, in any case. */
export function sameRepo(a, b) {
  if (!a || !b) return false;
  const key = (u) => (hostOf(u) ? teamRepoLabel({ url: u }).toLowerCase() : '');
  return Boolean(key(a)) && key(a) === key(b);
}

/** What goes on the team after an owner/admin connects: the URL without a user, and the provider. */
export function teamRepoFromLink(git) {
  return { url: withoutUser(git?.url || ''), provider: git?.provider || 'github' };
}

/** Does a connect request (from a dialog or Join) aim at the team's stored repository? */
export function targetsTeamRepo(teamRepo, { owner, repo, url } = {}) {
  if (!teamRepo?.url) return false;
  if (url) return sameRepo(url, teamRepo.url);
  const stored = ownerRepoOf(teamRepo.url);
  return Boolean(owner && repo) && stored.owner.toLowerCase() === String(owner).toLowerCase() && stored.repo.toLowerCase() === String(repo).toLowerCase();
}

/**
 * The signed-in accounts that can reach the team's repository: the GitHub
 * account for a github.com repository, the GitLab / Gitea accounts on its server.
 * "Other git server" (URL + Vault item) is not in it — that always asks.
 * @param {Array<{ key, provider, accountId, server? }>} accounts  connectableAccounts()
 */
export function accountsForTeamRepo(accounts = [], teamRepo) {
  const host = hostOf(teamRepo?.url || '');
  if (!host) return [];
  return accounts.filter((a) => {
    if (a.provider === 'github') return host === 'github.com';
    if (a.provider === 'gitlab') return hostOf(a.server || 'https://gitlab.com') === host;
    if (a.provider === 'gitea') return Boolean(a.server) && hostOf(a.server) === host;
    return false;
  });
}

/**
 * The connect options that join the team's repository with one account
 * (`{ provider, accountId }`) or one Vault item (`{ vaultItemId, vaultItemName }`).
 * Never creates a repository.
 */
export function joinOptions(teamRepo, signIn = {}) {
  const { owner, repo } = ownerRepoOf(teamRepo.url);
  if (signIn.vaultItemId) {
    return { owner, repo, provider: 'remote', url: teamRepo.url, vaultItemId: signIn.vaultItemId, vaultItemName: signIn.vaultItemName || '', createIfMissing: false };
  }
  return { owner, repo, provider: signIn.provider, accountId: signIn.accountId || undefined, createIfMissing: false };
}
